import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CodeIcon from "@mui/icons-material/Code";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { PageHeader, SectionCard } from "_shared/ui";

// Backend origin that serves the documentation endpoints + Swagger UI.
// The docs endpoints are public (no auth required).
const BASE = (process.env.REACT_APP_REQUEST_ENDPOINT || "").replace(/\/+$/, "");
const docUrl = (key, action) => `${BASE}/api/v1/docs/${key}/${action}`;

// The Event Data Dictionary is served directly from the S3 workbook (no local
// .md). We display it via Microsoft's Office online viewer and download the
// raw .xlsx straight from S3.
const EVENT_DICT_XLSX_URL =
  "https://gala-invite.s3.ap-south-1.amazonaws.com/MSFA_event_data_dictionary+(1).xlsx";
const officeViewerUrl = (fileUrl) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

const RESOURCES = [
  {
    id: "technical",
    title: "Technical Documentation",
    description:
      "Full MSFA technical documentation — product concepts plus the complete engineering reference.",
    Icon: MenuBookIcon,
    viewUrl: docUrl("technical", "view"),
    downloadMd: docUrl("technical", "download"),
    downloadPdf: docUrl("technical", "pdf"),
  },
  {
    id: "swagger",
    title: "API Documentation (Swagger)",
    description:
      "Live, endpoint-level OpenAPI reference served directly by the MSFA backend.",
    Icon: CodeIcon,
    viewUrl: `${BASE}/api/v1/docs/swagger`,
    externalUrl: `${BASE}/api/v1/docs/swagger`,
    externalLabel: "Open Swagger",
  },
  {
    id: "erd",
    title: "Entity Relationship Diagram",
    description: "Interactive ERD of the MSFA data model (hosted on Eraser).",
    Icon: AccountTreeIcon,
    viewUrl: `${BASE}/api/v1/docs/erd`,
    externalUrl:
      "https://app.eraser.io/workspace/z3AJDBEckuEYRBhHIyjS?diagram=jH5GNk3yaZPnSDOF8Zm4z",
    externalLabel: "Open in Eraser",
  },
  {
    id: "version-control",
    title: "Version Control & Environment",
    description:
      "Branching model, environments and release / deployment flow for MSFA.",
    Icon: DescriptionIcon,
    viewUrl: docUrl("version-control", "view"),
    downloadMd: docUrl("version-control", "download"),
    downloadPdf: docUrl("version-control", "pdf"),
  },
  {
    id: "event-data-dictionary",
    title: "Event Data Dictionary",
    description:
      "Complete analytics tracking plan: events, properties, enums, and naming conventions.",
    Icon: DescriptionIcon,
    // Rendered in-page via the Office online viewer; downloaded straight from S3.
    viewUrl: officeViewerUrl(EVENT_DICT_XLSX_URL),
    downloadXlsx: EVENT_DICT_XLSX_URL,
  },
];

const Main = () => {
  const resources = useMemo(() => RESOURCES, []);
  const [activeId, setActiveId] = useState("technical");
  const active = resources.find((r) => r.id === activeId) || resources[0];
  const ActiveIcon = active.Icon;

  return (
    <Box>
      <PageHeader
        title="Documentation"
        subtitle="Browse and download the latest technical documentation, API reference, data model (ERD) and version-control / environment guide. Everything is served live from the backend, so it always reflects the latest update."
      />

      {/* Resource cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {resources.map((r) => {
          const CardIcon = r.Icon;
          const isActive = r.id === activeId;
          return (
            <Grid item xs={12} sm={6} md={3} key={r.id}>
              <Card
                sx={{
                  height: "100%",
                  transition: "all .15s ease",
                  borderColor: isActive ? "primary.main" : "divider",
                  borderWidth: isActive ? 2 : 1,
                  boxShadow: (theme) =>
                    isActive ? theme.shadows[4] : theme.shadows[1],
                  "&:hover": {
                    boxShadow: (theme) => theme.shadows[3],
                  },
                }}
              >
                <CardActionArea
                  onClick={() => setActiveId(r.id)}
                  sx={{ height: "100%" }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          p: 0.8,
                          display: "inline-flex",
                          bgcolor: (theme) =>
                            isActive
                              ? "primary.main"
                              : alpha(theme.palette.primary.main, 0.12),
                          color: isActive
                            ? "primary.contrastText"
                            : "primary.main",
                        }}
                      >
                        <CardIcon fontSize="small" />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {r.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", minHeight: 48, mb: 1.5 }}
                    >
                      {r.description}
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveId(r.id);
                        }}
                      >
                        View
                      </Button>
                      {r.downloadMd && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          href={r.downloadMd}
                          onClick={(e) => e.stopPropagation()}
                        >
                          .md
                        </Button>
                      )}
                      {r.downloadXlsx && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          href={r.downloadXlsx}
                          onClick={(e) => e.stopPropagation()}
                        >
                          .xlsx
                        </Button>
                      )}
                      {r.downloadPdf && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PictureAsPdfIcon />}
                          href={r.downloadPdf}
                          onClick={(e) => e.stopPropagation()}
                        >
                          PDF
                        </Button>
                      )}
                      {r.externalUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          href={r.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.externalLabel || "Open"}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Embedded viewer */}
      <SectionCard
        disablePadding
        sx={{ overflow: "hidden" }}
        title={
          <Box
            component="span"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <ActiveIcon fontSize="small" sx={{ color: "primary.main" }} />
            {active.title}
          </Box>
        }
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            href={active.viewUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in new tab
          </Button>
        }
      >
        <Box
          component="iframe"
          key={active.id}
          src={active.viewUrl}
          title={active.title}
          sx={{
            width: "100%",
            height: "calc(100vh - 360px)",
            minHeight: 520,
            border: 0,
            display: "block",
          }}
        />
      </SectionCard>
    </Box>
  );
};

const Documentation = () => {
  return <DashGridL2 Top={TopBar} Main={Main} />;
};

export default Documentation;
